import React from 'react';
import { Beaker } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * DEMO badge — shown next to an order title / row / header whenever the
 * order is flagged `is_demo`. Purely cosmetic; the real behaviour
 * (email redirect, KPI exclusion, e-conomic + Shopify dry-run) lives on
 * the backend.
 *
 * Only visible to internal users (admin / super_admin) so the customer
 * stays unaware they are being demoed to. Non-admins render nothing.
 *
 * Props:
 *  - order:   the order object. If undefined or `is_demo != true` we
 *             render nothing.
 *  - size:    "sm" (default) | "md". `md` is a bigger banner-style pill
 *             for page headers.
 *  - className: extra classes to compose in.
 */
export function DemoBadge({ order, size = 'sm', className = '' }) {
    const { user } = useAuth();
    if (!order || order.is_demo !== true) return null;
    const role = user?.role;
    const isInternal = role === 'admin' || role === 'super_admin';
    if (!isInternal) return null;

    const sizes = {
        sm: 'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 gap-1',
        md: 'text-xs font-bold uppercase tracking-wider px-2 py-1 gap-1.5',
    };
    return (
        <span
            className={
                'inline-flex items-center rounded-md border border-amber-300 bg-amber-100 text-amber-800 ' +
                (sizes[size] || sizes.sm) +
                ' ' + className
            }
            title="Demo order — no external partners contacted, excluded from KPIs"
            data-testid="demo-order-badge"
        >
            <Beaker className={size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            DEMO
        </span>
    );
}

export default DemoBadge;
